import * as z from "zod";
import { VIA_BOOKMARK, VIA_PARAM } from "@/contracts/session";
import { storageKey } from "@/site";

export const UNCONFIRMED_READER_KEY = storageKey("unconfirmedReader.v1");
export const BOOKMARK_LANDING_KEY = storageKey("bookmarkLanding.v1");
/**
 * How long a bookmark landing waits for a session answer to gate. Long
 * enough for a slow first read in any tab; short enough that a scan whose
 * session never arrived cannot gate an unrelated reader later.
 */
export const BOOKMARK_LANDING_TTL_MS = 5 * 60 * 1000;

/** What signed the reader in: a scanned bookmark, or this device resuming it. */
const gateSourceSchema = z.enum(["bookmark", "device"]);
export type GateSource = z.infer<typeof gateSourceSchema>;

const markerSchema = z.object({
  readerId: z.uuid(),
  markedAt: z.number().finite(),
  /** Absent for a bookmark, the shape every earlier marker has. */
  source: gateSourceSchema.optional(),
});
const markersSchema = z.array(markerSchema);

export type UnconfirmedReader = z.infer<typeof markerSchema>;

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

const browserStorage = (): Store | null => {
  try {
    return localStorage;
  } catch {
    return null;
  }
};

const AUTH_ERROR_PARAM = "auth_error";

/**
 * Whether the URL is a bookmark scan's landing, one that signed a reader
 * in. A scan that failed lands with its error and no `via`, and a sign-in
 * that failed after the Worker chose the landing carries both; neither
 * gates, since no reader was scanned in.
 */
export function landedBookmark(search: string | URLSearchParams): boolean {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  return (
    params.get(VIA_PARAM) === VIA_BOOKMARK && !params.has(AUTH_ERROR_PARAM)
  );
}

const landingRecordSchema = z.object({ landedAt: z.number().finite() });

/**
 * Record that a bookmark scan landed, naming no reader, so that whichever
 * tab first hears a reader_session answer gates that reader: the landing
 * tab's own read may fail or lag while another tab already shows the
 * reader. Written the moment the landing is read.
 */
export function noteBookmarkLanding(
  now: number,
  storage: Store | null = browserStorage(),
): boolean {
  // Runs while the session store is built at boot: a refused write must
  // cost only the cross-tab record, never the app.
  try {
    storage?.setItem(BOOKMARK_LANDING_KEY, JSON.stringify({ landedAt: now }));
    return storage !== null;
  } catch {
    return false;
  }
}

/** Spend the landing record: true once, while it is younger than the TTL. */
export function takeBookmarkLanding(
  now: number,
  storage: Store | null = browserStorage(),
): boolean {
  const raw = storage?.getItem(BOOKMARK_LANDING_KEY);
  if (!raw) return false;
  storage?.removeItem(BOOKMARK_LANDING_KEY);
  try {
    const parsed = landingRecordSchema.safeParse(JSON.parse(raw));
    return (
      parsed.success &&
      now >= parsed.data.landedAt &&
      now - parsed.data.landedAt < BOOKMARK_LANDING_TTL_MS
    );
  } catch {
    return false;
  }
}

/** Every reader still awaiting "That's me"; a lone marker is the old shape. */
export function unconfirmedReaders(
  storage: Store | null = browserStorage(),
): UnconfirmedReader[] {
  const raw = storage?.getItem(UNCONFIRMED_READER_KEY);
  if (!raw) return [];
  try {
    const stored: unknown = JSON.parse(raw);
    const parsed = markersSchema.safeParse(
      Array.isArray(stored) ? stored : [stored],
    );
    if (parsed.success) return parsed.data;
  } catch {
    // Unparseable: treated as no marker, and cleared below.
  }
  storage?.removeItem(UNCONFIRMED_READER_KEY);
  return [];
}

function store(markers: UnconfirmedReader[], storage: Store | null): void {
  if (markers.length === 0) storage?.removeItem(UNCONFIRMED_READER_KEY);
  else storage?.setItem(UNCONFIRMED_READER_KEY, JSON.stringify(markers));
}

// No cap: dropping a marker lifts that reader's gate, and "That's me" or
// "Not me" is what takes one off.
function mark(marker: UnconfirmedReader, storage: Store | null): void {
  const others = unconfirmedReaders(storage).filter(
    (other) => other.readerId !== marker.readerId,
  );
  store([...others, marker], storage);
}

/**
 * A bookmark scan signs in by a printed readerId or token alone, so the
 * reader it signed in stays unconfirmed, across reloads and tabs, until
 * someone says "That's me". Markers accumulate rather than replace: a stale
 * landing (Back, an old tab) can add a reader to confirm, never lift another
 * reader's gate.
 */
export function markUnconfirmed(
  readerId: string,
  now: number,
  storage: Store | null = browserStorage(),
): void {
  mark({ readerId, markedAt: now }, storage);
}

/** A device reader resumed without an account, held like a scan. */
export function markDeviceUnconfirmed(
  readerId: string,
  now: number,
  storage: Store | null = browserStorage(),
): void {
  mark({ readerId, markedAt: now, source: "device" }, storage);
}

/** Why the reader awaits "That's me"; null when it does not. */
export function unconfirmedSource(
  readerId: string,
  storage: Store | null = browserStorage(),
): GateSource | null {
  const marker = unconfirmedReaders(storage).find(
    (candidate) => candidate.readerId === readerId,
  );
  if (marker === undefined) return null;
  return marker.source ?? "bookmark";
}

export function isUnconfirmed(
  readerId: string,
  storage: Store | null = browserStorage(),
): boolean {
  return unconfirmedReaders(storage).some(
    (marker) => marker.readerId === readerId,
  );
}

export function clearUnconfirmed(
  readerId: string,
  storage: Store | null = browserStorage(),
): void {
  store(
    unconfirmedReaders(storage).filter(
      (marker) => marker.readerId !== readerId,
    ),
    storage,
  );
}
